/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { createContext, useState, useCallback, ReactNode } from "react";

import { useApiConfig } from "../api-config";

import { fetchAccount } from "./account.fetch";
import { Account } from "./account.hooks";

export interface AccountContextType {
	account: Account;
	isLoading: boolean;
	refreshAccount: () => Promise<void>;
	/**
	 * Replace the account without a round trip, for callers that already hold a
	 * server-verified copy. `/api/cosmos/auth` returns one, so the client can
	 * skip a second fetch and can never disagree with the session that request
	 * just validated.
	 */
	setAccount: (account: Account) => void;
}

export const AccountContext = createContext<AccountContextType | undefined>(undefined);

export function AccountProvider({
	initialValue,
	children,
}: {
	initialValue: Account;
	children: ReactNode;
}): React.ReactElement {
	const { serverUrl } = useApiConfig();
	const [account, setAccount] = useState<Account>(initialValue);
	const [isLoading, setIsLoading] = useState(false);

	const refreshAccount = useCallback(async () => {
		setIsLoading(true);
		try {
			const freshAccount = await fetchAccount(serverUrl);
			setAccount(freshAccount);
		} catch (error) {
			console.error("Failed to refresh account:", error);
		} finally {
			setIsLoading(false);
		}
	}, [serverUrl]);

	return (
		<AccountContext.Provider value={{ account, isLoading, refreshAccount, setAccount }}>
			{children}
		</AccountContext.Provider>
	);
}
